import { HttpClient } from "@angular/common/http";
import { inject } from "@angular/core";
import {
    Firm360HttpContext,
    type HttpRetryPolicy,
} from "@firm360/libs/api/http";
import { type Observable, map } from "rxjs";
import type { PendoProfile } from "../../pendo-profile.model";
import type { PendoProfileSource } from "../pendo-profile-source";

/**
 * Rides out a blip quickly, then waits an outage out rather than adding to
 * it. One endpoint is asked at most once more than the schedule has waits.
 * No ceiling: nobody waits on this in the background, so it honors however
 * long the server asks to be left alone.
 */
export const PENDO_PROFILE_RETRY_POLICY = {
    schedule: [500, 1000, 2000, 30_000, 60_000, 120_000],
    maxDelayMs: Number.POSITIVE_INFINITY,
} as const satisfies HttpRetryPolicy;

/** Bounds each attempt, so a hung request doesn't stall analytics. */
export const PENDO_PROFILE_ATTEMPT_TIMEOUT_MS = 10_000;

/**
 * A profile from one server endpoint, fetched as analytics must be: quietly,
 * each attempt bounded by {@link PENDO_PROFILE_ATTEMPT_TIMEOUT_MS}, and
 * retried under {@link PENDO_PROFILE_RETRY_POLICY}, by the app's retry and
 * timeout interceptors. Errors once that gives up, or when the response
 * can't be translated.
 */
export abstract class AbstractServerProfileSource<
    Response,
> implements PendoProfileSource {
    private readonly _http = inject(HttpClient);

    protected abstract readonly url: string;

    /** Throws when the response doesn't identify the session. */
    protected abstract translateResponse(response: Response): PendoProfile;

    contribute$(): Observable<PendoProfile> {
        return this._fetch$(
            new Firm360HttpContext().retry(PENDO_PROFILE_RETRY_POLICY),
        );
    }

    /** A single attempt, never retried. */
    contributeOnce$(): Observable<PendoProfile> {
        return this._fetch$(new Firm360HttpContext().noRetry());
    }

    /**
     * Failures come back to the caller rather than redirecting or surfacing in
     * the UI.
     */
    private _fetch$(context: Firm360HttpContext): Observable<PendoProfile> {
        return this._http
            .get<Response>(this.url, {
                context: context
                    .throwErrors()
                    .timeout(PENDO_PROFILE_ATTEMPT_TIMEOUT_MS),
            })
            .pipe(map((response) => this.translateResponse(response)));
    }
}
